import { getLighterColor } from "@/utils/colors";
import type { QuoteColorSchema, QuoteUiStyle } from "@/@sdk/brands/brand-model";
import type { QuoteThemeClassNames } from "./quote-theme-types";

const HEADING_FONT_CLASS = "[font-family:var(--quote-font-heading)]";
const DUAL_TONE_STYLE_A_SECTION_HEADING =
  `text-xl font-semibold text-[color:var(--quote-color-primary)] ${HEADING_FONT_CLASS}`;

const UI_STYLE_CLASS_MAP: Record<QuoteUiStyle, QuoteThemeClassNames> = {
  STYLE_A: {
    card: "rounded-xl border border-gray-100 bg-white shadow-sm",
    section: "space-y-4",
    sectionHeading: `text-xl font-semibold text-gray-900 ${HEADING_FONT_CLASS}`,
    pageBackground: "bg-gray-50",
    header: "sticky top-0 z-50 border-b border-gray-100 bg-white/80 shadow-sm backdrop-blur-md",
    greetingsCard: "overflow-hidden bg-white shadow-sm",
    heroBanner: "rounded-3xl",
    heroBannerWide: "rounded-b-3xl",
    itinerarySummary: "rounded-lg border border-gray-100 bg-white p-3 shadow-sm sm:p-4",
    pricingElevatedCard: "rounded-xl border border-gray-100 bg-white p-4 shadow-md sm:p-6",
  },
  STYLE_B: {
    card: "rounded-2xl bg-white/90 shadow-none",
    section: "space-y-5 rounded-2xl p-1",
    sectionHeading: `text-xl font-semibold text-gray-900 ${HEADING_FONT_CLASS}`,
    pageBackground: "bg-gray-50",
    header:
      "sticky top-0 z-50 border-b border-transparent bg-[color:var(--quote-color-secondary)]/90 backdrop-blur-md",
    greetingsCard: "overflow-hidden rounded-2xl bg-[color:var(--quote-color-secondary)] shadow-none",
    heroBanner: "rounded-2xl",
    heroBannerWide: "rounded-b-2xl",
    itinerarySummary:
      "rounded-2xl bg-[color:var(--quote-color-secondary)] p-3 shadow-none sm:p-4",
    pricingElevatedCard:
      "rounded-2xl bg-[color:var(--quote-color-secondary)] p-4 shadow-none sm:p-6",
  },
  STYLE_C: {
    card: "rounded-lg border border-gray-200 bg-white shadow-none",
    section: "space-y-4 border-l-4 border-[color:var(--quote-color-primary)] pl-4",
    sectionHeading:
      `text-2xl font-semibold tracking-tight text-gray-900 ${HEADING_FONT_CLASS}`,
    pageBackground: "bg-white",
    header: "sticky top-0 z-50 border-b border-gray-200 bg-white",
    greetingsCard: "overflow-hidden border border-gray-200 bg-white shadow-none",
    heroBanner: "rounded-lg",
    heroBannerWide: "rounded-none sm:rounded-b-lg",
    itinerarySummary:
      "rounded-lg border border-gray-200 bg-white p-3 shadow-none sm:p-4",
    pricingElevatedCard:
      "rounded-lg border border-gray-200 bg-white p-4 shadow-lg sm:p-6",
  },
};

const UI_STYLE_TOKENS: Record<
  QuoteUiStyle,
  Pick<
    QuoteThemeClassNames,
    never
  > & {
    radiusCard: string;
    radiusPill: string;
    shadowCard: string;
    sectionGap: string;
  }
> = {
  STYLE_A: {
    radiusCard: "1rem",
    radiusPill: "9999px",
    shadowCard: "0 1px 3px rgba(0, 0, 0, 0.08)",
    sectionGap: "2rem",
  },
  STYLE_B: {
    radiusCard: "1.25rem",
    radiusPill: "9999px",
    shadowCard: "none",
    sectionGap: "2.25rem",
  },
  STYLE_C: {
    radiusCard: "0.5rem",
    radiusPill: "0.375rem",
    shadowCard: "none",
    sectionGap: "2.5rem",
  },
};

export function getQuoteThemeClassNames(
  uiStyle: QuoteUiStyle,
  colorSchema?: QuoteColorSchema
): QuoteThemeClassNames {
  const base = UI_STYLE_CLASS_MAP[uiStyle];

  if (uiStyle === "STYLE_A" && colorSchema === "DUAL_TONE") {
    return {
      ...base,
      sectionHeading: DUAL_TONE_STYLE_A_SECTION_HEADING,
    };
  }

  return base;
}

export function getColorLuminance(hexColor: string): number {
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/** Secondary when readable on white; otherwise primary. Used for Style A dual-tone headings. */
export function getDualToneBlockHeadingColor(
  primary: string,
  secondary: string
): string {
  return getColorLuminance(secondary) < 0.72 ? secondary : primary;
}

export function getQuoteThemeStyleTokens(uiStyle: QuoteUiStyle) {
  return UI_STYLE_TOKENS[uiStyle];
}

export function deriveSecondaryColor(
  primary: string,
  configuredSecondary?: string,
  colorSchema: "SINGLE_TONE" | "DUAL_TONE" = "SINGLE_TONE"
): string {
  if (colorSchema === "DUAL_TONE" && configuredSecondary) {
    return configuredSecondary;
  }

  return getLighterColor(primary, 0.08);
}

export function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#1f2937" : "#ffffff";
}

const SCRIPT_GOOGLE_FONTS = new Set(["Dancing Script", "Great Vibes"]);

function buildGoogleFontFamilyParam(family: string): string {
  const encoded = encodeURIComponent(family).replace(/%20/g, "+");
  if (SCRIPT_GOOGLE_FONTS.has(family)) {
    return `family=${encoded}`;
  }
  return `family=${encoded}:wght@400;500;600;700`;
}

export function buildGoogleFontsUrlForFamilies(families: string[]): string {
  const uniqueFamilies = [...new Set(families.filter(Boolean))];
  const params = uniqueFamilies.map(buildGoogleFontFamilyParam).join("&");
  return `https://fonts.googleapis.com/css2?${params}&display=swap`;
}

export function buildGoogleFontsUrl(
  headingFont: string,
  bodyFont: string
): string {
  return buildGoogleFontsUrlForFamilies([headingFont, bodyFont]);
}

export function formatFontFamily(font: string): string {
  return font.includes(" ") ? `"${font}", sans-serif` : `${font}, sans-serif`;
}

const SCRIBBLE_UNDERLINE_MASK = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 6" preserveAspectRatio="none">` +
    `<path d="M1 3.4 C28 2.6 52 3.8 78 3.1 S128 2.4 154 3.5 S182 3.8 199 3.2" fill="none" stroke="white" stroke-width="1.6" stroke-linecap="round"/>` +
    `</svg>`
);

export const DUAL_TONE_STYLE_A_HEADING_CSS = `
  .quote-theme-root[data-color-schema="DUAL_TONE"][data-ui-style="STYLE_A"] :is(h2, h3):not(.text-xs):not(.text-sm):not(.quote-itinerary-item-title):not(.text-gray-500):not(.quote-custom-template-block *):not(.quote-footer *) {
    color: var(--quote-color-block-heading) !important;
    position: relative;
    width: fit-content;
    max-width: 100%;
    padding-bottom: 0.3rem;
    border-bottom: none;
  }
  .quote-theme-root[data-color-schema="DUAL_TONE"][data-ui-style="STYLE_A"] :is(h2, h3):not(.text-xs):not(.text-sm):not(.quote-itinerary-item-title):not(.text-gray-500):not(.quote-custom-template-block *):not(.quote-footer *)::after {
    content: "";
    position: absolute;
    left: 0;
    right: -2%;
    bottom: 0.12em;
    height: 0.28em;
    min-height: 4px;
    max-height: 6px;
    background-color: var(--quote-color-secondary);
    opacity: 0.72;
    mask-image: url("data:image/svg+xml,${SCRIBBLE_UNDERLINE_MASK}");
    -webkit-mask-image: url("data:image/svg+xml,${SCRIBBLE_UNDERLINE_MASK}");
    mask-size: 100% 100%;
    -webkit-mask-size: 100% 100%;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
    pointer-events: none;
  }
`;
